import * as THREE from 'three';

export default class InstanceMeshManager {
    constructor(scene) {
        this.scene = scene;
        this.instancedMeshes = {};
        this.geometries = {};
        this.materials = {};
        this.initialized = false;
    }

    initialize() {
        if (this.initialized) return;
        
        // Initialize geometries
        this.initializeGeometries();
        
        // Initialize materials
        this.initializeMaterials();
        
        // Create instanced meshes
        this.createInstancedMeshes();
        
        this.initialized = true;
    }

    initializeGeometries() {
        // Tree geometries
        this.geometries.treeTrunk = new THREE.CylinderGeometry(0.3, 0.4, 1, 5);
        this.geometries.coniferFoliage = new THREE.ConeGeometry(1, 1, 6);
        this.geometries.broadleafFoliage = new THREE.IcosahedronGeometry(1, 0);
        
        // Rock geometries
        this.geometries.rockTypes = [
            new THREE.DodecahedronGeometry(1, 0),
            new THREE.OctahedronGeometry(1, 0),
            new THREE.TetrahedronGeometry(1, 0),
            new THREE.IcosahedronGeometry(1, 0)
        ];
        
        // Cactus geometries
        this.geometries.cactusTrunk = new THREE.CylinderGeometry(0.4, 0.5, 1, 8);
        this.geometries.cactusArm = new THREE.CylinderGeometry(0.25, 0.3, 1, 8);
        this.geometries.barrelCactus = new THREE.CapsuleGeometry(1, 1, 8, 8);
        this.geometries.pricklyCactusPad = new THREE.SphereGeometry(1, 8, 8);
        // Flatten the sphere to make it pad-like
        this.geometries.pricklyCactusPad.scale(0.5, 0.8, 0.2);
    }

    initializeMaterials() {
        // Tree materials
        this.materials.treeTrunk = new THREE.MeshStandardMaterial({
            color: 0x8B4513,
            roughness: 0.9,
            metalness: 0.1,
            flatShading: true
        });

        this.materials.coniferFoliage = new THREE.MeshStandardMaterial({
            color: 0x2E8B57,
            roughness: 1.0,
            metalness: 0.0,
            flatShading: true
        });

        this.materials.broadleafFoliage = new THREE.MeshStandardMaterial({
            color: 0x32CD32,
            roughness: 1.0,
            metalness: 0.0,
            flatShading: true
        });

        // Rock material
        this.materials.rock = new THREE.MeshStandardMaterial({
            color: 0x808080,
            roughness: 0.9,
            metalness: 0.1,
            flatShading: true
        });

        // Cactus materials
        this.materials.cactus = new THREE.MeshStandardMaterial({
            color: 0x2F4F2F,
            roughness: 0.8,
            metalness: 0.1,
            flatShading: true
        });
    }

    createInstancedMeshes() {
        // Maximum instance counts
        const MAX_TREES = 2000;
        const MAX_ROCKS = 1000;
        const MAX_CACTI = 500;
        
        // Create tree instanced meshes
        this.instancedMeshes.treeTrunk = new THREE.InstancedMesh(
            this.geometries.treeTrunk,
            this.materials.treeTrunk,
            MAX_TREES
        );
        
        this.instancedMeshes.coniferFoliage = new THREE.InstancedMesh(
            this.geometries.coniferFoliage,
            this.materials.coniferFoliage,
            MAX_TREES
        );
        
        this.instancedMeshes.broadleafFoliage = new THREE.InstancedMesh(
            this.geometries.broadleafFoliage,
            this.materials.broadleafFoliage,
            MAX_TREES * 3
        );
        
        // Create rock instanced meshes for each type
        this.instancedMeshes.rocks = [];
        for (let i = 0; i < this.geometries.rockTypes.length; i++) {
            const rockMesh = new THREE.InstancedMesh(
                this.geometries.rockTypes[i],
                this.materials.rock,
                MAX_ROCKS
            );
            this.instancedMeshes.rocks.push(rockMesh);
            this.scene.add(rockMesh);
            rockMesh.count = 0; // Initialize count
        }
        
        // Create cactus instanced meshes
        this.instancedMeshes.cactusTrunk = new THREE.InstancedMesh(
            this.geometries.cactusTrunk,
            this.materials.cactus,
            MAX_CACTI
        );
        
        this.instancedMeshes.cactusArm = new THREE.InstancedMesh(
            this.geometries.cactusArm,
            this.materials.cactus,
            MAX_CACTI * 2
        );
        
        this.instancedMeshes.barrelCactus = new THREE.InstancedMesh(
            this.geometries.barrelCactus,
            this.materials.cactus,
            MAX_CACTI
        );
        
        this.instancedMeshes.pricklyCactusPad = new THREE.InstancedMesh(
            this.geometries.pricklyCactusPad,
            this.materials.cactus,
            MAX_CACTI * 4
        );
        
        // Initialize counts
        this.instancedMeshes.treeTrunk.count = 0;
        this.instancedMeshes.coniferFoliage.count = 0;
        this.instancedMeshes.broadleafFoliage.count = 0;
        this.instancedMeshes.cactusTrunk.count = 0;
        this.instancedMeshes.cactusArm.count = 0;
        this.instancedMeshes.barrelCactus.count = 0;
        this.instancedMeshes.pricklyCactusPad.count = 0;
        
        // Add meshes to scene
        this.scene.add(this.instancedMeshes.treeTrunk);
        this.scene.add(this.instancedMeshes.coniferFoliage);
        this.scene.add(this.instancedMeshes.broadleafFoliage);
        this.scene.add(this.instancedMeshes.cactusTrunk);
        this.scene.add(this.instancedMeshes.cactusArm);
        this.scene.add(this.instancedMeshes.barrelCactus);
        this.scene.add(this.instancedMeshes.pricklyCactusPad);
        
        // Enable casting shadows
        this.instancedMeshes.treeTrunk.castShadow = true;
        this.instancedMeshes.coniferFoliage.castShadow = true;
        this.instancedMeshes.broadleafFoliage.castShadow = true;
        this.instancedMeshes.cactusTrunk.castShadow = true;
        this.instancedMeshes.cactusArm.castShadow = true;
        this.instancedMeshes.barrelCactus.castShadow = true;
        this.instancedMeshes.pricklyCactusPad.castShadow = true;
        
        // Enable receiving shadows
        this.instancedMeshes.treeTrunk.receiveShadow = true;
        this.instancedMeshes.coniferFoliage.receiveShadow = true;
        this.instancedMeshes.broadleafFoliage.receiveShadow = true;
        this.instancedMeshes.cactusTrunk.receiveShadow = true;
        this.instancedMeshes.cactusArm.receiveShadow = true;
        this.instancedMeshes.barrelCactus.receiveShadow = true;
        this.instancedMeshes.pricklyCactusPad.receiveShadow = true;
    }
    
    addTree(position, treeType, height) {
        const matrix = new THREE.Matrix4();
        const tempMatrix = new THREE.Matrix4();
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap ? heightMap.getHeight(position.x, position.z) : 0;

        if (treeType === 'conifer') {
            // Add trunk
            const trunkMatrix = tempMatrix.clone();
            const trunkScale = new THREE.Vector3(1, height * 0.8, 1);
            trunkMatrix.compose(
                new THREE.Vector3(position.x, terrainY + height * 0.4, position.z),
                new THREE.Quaternion(),
                trunkScale
            );
            this.instancedMeshes.treeTrunk.setMatrixAt(this.instancedMeshes.treeTrunk.count, trunkMatrix);
            this.instancedMeshes.treeTrunk.count++;

            // Add foliage
            const foliageMatrix = tempMatrix.clone();
            const foliageScale = new THREE.Vector3(
                height * 0.6,
                height * 0.9,
                height * 0.6
            );
            const foliageRotation = new THREE.Quaternion().setFromAxisAngle(
                new THREE.Vector3(0, 1, 0),
                Math.random() * Math.PI
            );
            foliageMatrix.compose(
                new THREE.Vector3(position.x, terrainY + height * 0.8, position.z),
                foliageRotation,
                foliageScale
            );
            this.instancedMeshes.coniferFoliage.setMatrixAt(this.instancedMeshes.coniferFoliage.count, foliageMatrix);
            this.instancedMeshes.coniferFoliage.count++;
        } else {
            // Broadleaf tree
            // Add trunk
            const trunkMatrix = tempMatrix.clone();
            const trunkScale = new THREE.Vector3(1, height * 0.5, 1);
            const trunkRotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(
                (Math.random() - 0.5) * 0.2,
                0,
                (Math.random() - 0.5) * 0.2
            ));
            trunkMatrix.compose(
                new THREE.Vector3(position.x, terrainY + height * 0.25, position.z),
                trunkRotation,
                trunkScale
            );
            this.instancedMeshes.treeTrunk.setMatrixAt(this.instancedMeshes.treeTrunk.count, trunkMatrix);
            this.instancedMeshes.treeTrunk.count++;

            // Add multiple foliage clusters
            const foliageClusters = 3 + Math.floor(Math.random() * 3);

            for (let i = 0; i < foliageClusters; i++) {
                const foliageMatrix = tempMatrix.clone();

                // Calculate position within the crown of the tree
                const angle = Math.random() * Math.PI * 2;
                const radiusXZ = 0.1 + Math.random() * 0.5;
                const offsetX = Math.cos(angle) * radiusXZ;
                const offsetZ = Math.sin(angle) * radiusXZ;

                // Higher clusters are higher up and smaller
                const verticalPosition = terrainY + height * 0.6 + (i * height * 0.15);
                const foliageSize = height * (0.4 - i * 0.05);

                const foliageScale = new THREE.Vector3(foliageSize, foliageSize, foliageSize);
                const foliageRotation = new THREE.Quaternion().setFromAxisAngle(
                    new THREE.Vector3(0, 1, 0),
                    Math.random() * Math.PI
                );

                foliageMatrix.compose(
                    new THREE.Vector3(position.x + offsetX, verticalPosition, position.z + offsetZ),
                    foliageRotation,
                    foliageScale
                );

                this.instancedMeshes.broadleafFoliage.setMatrixAt(this.instancedMeshes.broadleafFoliage.count, foliageMatrix);
                this.instancedMeshes.broadleafFoliage.count++;
            }
        }
        
        this.instancedMeshes.treeTrunk.instanceMatrix.needsUpdate = true;
        this.instancedMeshes.coniferFoliage.instanceMatrix.needsUpdate = true;
        this.instancedMeshes.broadleafFoliage.instanceMatrix.needsUpdate = true;
    }
    
    addRock(position, sizeFactor, isLargeFormation = false) {
        const matrix = new THREE.Matrix4();
        
        // Base size of the rock
        const size = isLargeFormation 
            ? (8 + Math.random() * 12) * sizeFactor // Much larger for formations
            : (0.5 + Math.random() * 2) * sizeFactor;
            
        // Choose rock type (geometry)
        const rockTypeIndex = Math.floor(Math.random() * this.instancedMeshes.rocks.length);
        const instancedRock = this.instancedMeshes.rocks[rockTypeIndex];
        
        // Random non-uniform scale
        let scaleX, scaleY, scaleZ;
        if (isLargeFormation) {
            // More subtle variations for large formations
            scaleX = size * (0.9 + Math.random() * 0.2);
            scaleY = size * 1.0; // Keep height consistent
            scaleZ = size * (0.9 + Math.random() * 0.2);
        } else {
            // Random non-uniform scale for regular rocks
            scaleX = size * (0.8 + Math.random() * 0.4);
            scaleY = size * (0.8 + Math.random() * 0.4);
            scaleZ = size * (0.8 + Math.random() * 0.4);
        }
        
        // Ground embedding
        const groundOffset = isLargeFormation ? size * 0.1 : size * 0.3;
        
        // Random rotation
        const rotation = new THREE.Quaternion();
        if (isLargeFormation) {
            // Only rotate around Y axis for formations
            rotation.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.random() * Math.PI * 2);
        } else {
            // Random rotation in all axes for regular rocks
            rotation.setFromEuler(new THREE.Euler(
                Math.random() * Math.PI,
                Math.random() * Math.PI,
                Math.random() * Math.PI
            ));
        }
        
        const rockHeightMap = this.scene?.userData?.terrainHeightMap;
        const rockTerrainY = rockHeightMap ? rockHeightMap.getHeight(position.x, position.z) : 0;
        matrix.compose(
            new THREE.Vector3(position.x, rockTerrainY + size * 0.5 - groundOffset, position.z),
            rotation,
            new THREE.Vector3(scaleX, scaleY, scaleZ)
        );
        
        instancedRock.setMatrixAt(instancedRock.count, matrix);
        instancedRock.count++;
        instancedRock.instanceMatrix.needsUpdate = true;
        
        return size;
    }
    
    addCactus(position, sizeFactor, style) {
        // Choose cactus style if random
        if (style === 'random') {
            const styles = ['saguaro', 'barrel', 'prickly'];
            style = styles[Math.floor(Math.random() * styles.length)];
        }
        
        const matrix = new THREE.Matrix4();
        
        switch(style) {
            case 'saguaro':
                this.addSaguaroCactus(position, sizeFactor);
                break;
            case 'barrel':
                this.addBarrelCactus(position, sizeFactor);
                break;
            case 'prickly':
                this.addPricklyCactus(position, sizeFactor);
                break;
            default:
                this.addSaguaroCactus(position, sizeFactor);
        }
    }
    
    addSaguaroCactus(position, sizeFactor) {
        const matrix = new THREE.Matrix4();
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap ? heightMap.getHeight(position.x, position.z) : 0;

        // Base height with variation
        const height = (3 + Math.random() * 2) * sizeFactor;

        // Create main trunk
        const trunkMatrix = new THREE.Matrix4();
        trunkMatrix.compose(
            new THREE.Vector3(position.x, terrainY + height / 2, position.z),
            new THREE.Quaternion(),
            new THREE.Vector3(0.4 * sizeFactor, height, 0.4 * sizeFactor)
        );

        this.instancedMeshes.cactusTrunk.setMatrixAt(this.instancedMeshes.cactusTrunk.count, trunkMatrix);
        this.instancedMeshes.cactusTrunk.count++;

        // Add arms with probability
        if (Math.random() > 0.3) {
            // Left arm
            const leftArmHeight = height * (0.4 + Math.random() * 0.3);
            const leftArmY = height * (0.5 + Math.random() * 0.2);

            const leftArmRotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(
                0,
                0,
                Math.PI / 4 + (Math.random() * 0.2)
            ));

            const leftArmMatrix = new THREE.Matrix4();
            leftArmMatrix.compose(
                new THREE.Vector3(
                    position.x + 0.5 * sizeFactor,
                    terrainY + leftArmY + leftArmHeight / 2,
                    position.z
                ),
                leftArmRotation,
                new THREE.Vector3(0.25 * sizeFactor, leftArmHeight, 0.25 * sizeFactor)
            );

            this.instancedMeshes.cactusArm.setMatrixAt(this.instancedMeshes.cactusArm.count, leftArmMatrix);
            this.instancedMeshes.cactusArm.count++;

            // Maybe add right arm too
            if (Math.random() > 0.4) {
                const rightArmHeight = height * (0.3 + Math.random() * 0.3);
                const rightArmY = height * (0.4 + Math.random() * 0.2);

                const rightArmRotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(
                    0,
                    0,
                    -Math.PI / 4 - (Math.random() * 0.2)
                ));

                const rightArmMatrix = new THREE.Matrix4();
                rightArmMatrix.compose(
                    new THREE.Vector3(
                        position.x - 0.5 * sizeFactor,
                        terrainY + rightArmY + rightArmHeight / 2,
                        position.z
                    ),
                    rightArmRotation,
                    new THREE.Vector3(0.25 * sizeFactor, rightArmHeight, 0.25 * sizeFactor)
                );

                this.instancedMeshes.cactusArm.setMatrixAt(this.instancedMeshes.cactusArm.count, rightArmMatrix);
                this.instancedMeshes.cactusArm.count++;
            }
        }
        
        this.instancedMeshes.cactusTrunk.instanceMatrix.needsUpdate = true;
        this.instancedMeshes.cactusArm.instanceMatrix.needsUpdate = true;
    }
    
    addBarrelCactus(position, sizeFactor) {
        const matrix = new THREE.Matrix4();
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap ? heightMap.getHeight(position.x, position.z) : 0;

        // Base size with variation
        const radius = (0.8 + Math.random() * 0.6) * sizeFactor;
        const height = (1.2 + Math.random() * 0.8) * sizeFactor;

        matrix.compose(
            new THREE.Vector3(position.x, terrainY + height / 2, position.z),
            new THREE.Quaternion(),
            new THREE.Vector3(radius, height, radius)
        );
        
        this.instancedMeshes.barrelCactus.setMatrixAt(this.instancedMeshes.barrelCactus.count, matrix);
        this.instancedMeshes.barrelCactus.count++;
        this.instancedMeshes.barrelCactus.instanceMatrix.needsUpdate = true;
    }
    
    addPricklyCactus(position, sizeFactor) {
        // Base size with variation
        const height = (1.0 + Math.random() * 1.0) * sizeFactor;
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap ? heightMap.getHeight(position.x, position.z) : 0;

        // Create prickly pear pads
        const numPads = 3 + Math.floor(Math.random() * 4);

        for (let i = 0; i < numPads; i++) {
            const padMatrix = new THREE.Matrix4();

            // Position pads relative to each other
            const angle = (i / numPads) * Math.PI * 2;
            const radius = 0.3 * sizeFactor;
            const padScale = 0.8 * sizeFactor;

            const padRotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(
                Math.random() * 0.3,
                angle + Math.PI / 2,
                0
            ));

            padMatrix.compose(
                new THREE.Vector3(
                    position.x + Math.cos(angle) * radius,
                    terrainY + 0.8 * height * (0.5 + (i / numPads) * 0.5),
                    position.z + Math.sin(angle) * radius
                ),
                padRotation,
                new THREE.Vector3(padScale, padScale, padScale)
            );
            
            this.instancedMeshes.pricklyCactusPad.setMatrixAt(this.instancedMeshes.pricklyCactusPad.count, padMatrix);
            this.instancedMeshes.pricklyCactusPad.count++;
        }
        
        this.instancedMeshes.pricklyCactusPad.instanceMatrix.needsUpdate = true;
    }
} 